import type { CrmContact, CrmRecordType, CrmTagCategory, CrmTagType } from "@/lib/api/crm/masters";
import type { CrmRules, CrmTagRules } from "@/lib/api/crm/rules";
import type { SelectOptionItem } from "@/lib/models/ui";

/** What a tag would be attached to: a record itself, or one of its contacts or addresses. */
export type CrmTagTarget = {
  type: CrmTagType;
  /** The record's type (the owning record's, for contacts and addresses). */
  recordType: CrmRecordType;
  /** Contacts only. */
  contactKind?: CrmContact["kind"];
};

/** Whether GET /crm/rules allows the tag on the target. */
function isTagAllowed(tag: CrmTagRules, target: CrmTagTarget): boolean {
  if (tag.type !== target.type) return false;
  if (tag.record_types !== null && !tag.record_types.includes(target.recordType)) return false;
  if (target.contactKind && tag.contact_kinds !== null && !tag.contact_kinds.includes(target.contactKind)) return false;
  return true;
}

function categoryLabel(category: CrmTagCategory): string {
  const words = category.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "General" first, then the other categories alphabetically. */
function compareCategories(a: CrmTagCategory, b: CrmTagCategory): number {
  if (a === b) return 0;
  if (a === "general") return -1;
  if (b === "general") return 1;
  return a.localeCompare(b);
}

/** Picker options for the tags allowed on the target, grouped by category (shown in the label). */
export function tagOptions(rules: CrmRules, target: CrmTagTarget): SelectOptionItem<string>[] {
  return Object.entries(rules.tags)
    .filter(([, tag]) => isTagAllowed(tag, target))
    .sort(([, a], [, b]) => compareCategories(a.category, b.category) || a.name.localeCompare(b.name))
    .map(([id, tag]) => ({
      value: id,
      label: tag.category === "general" ? tag.name : `${tag.name} (${categoryLabel(tag.category)})`,
    }));
}

/** Splits tag ids into the ones still allowed on the target and the ones that aren't. */
export function splitAllowedTagIds(
  rules: CrmRules,
  tagIds: string[],
  target: CrmTagTarget,
): { allowed: string[]; removed: string[] } {
  const allowed: string[] = [];
  const removed: string[] = [];
  for (const tagId of tagIds) {
    const tag = rules.tags[tagId];
    if (tag && isTagAllowed(tag, target)) {
      allowed.push(tagId);
    } else {
      removed.push(tagId);
    }
  }
  return { allowed, removed };
}

/** The id of a code catalog tag (e.g. contact "primary"), or null when GET /crm/rules doesn't have it. */
export function catalogTagId(rules: CrmRules, type: CrmTagType, key: string): string | null {
  return Object.entries(rules.tags).find(([, tag]) => tag.type === type && tag.key === key)?.[0] ?? null;
}

export function tagNames(rules: CrmRules, tagIds: string[]): string {
  return tagIds.map((tagId) => rules.tags[tagId]?.name ?? "Unknown tag").join(", ");
}

/** "Only one email can be Primary": the message for a one-per-contact-kind tag on two contacts of a kind. */
export function onePerContactKindMessage(rules: CrmRules, kind: CrmContact["kind"], tagIds: string[]): string {
  return `Only one ${kind} can be ${tagNames(rules, tagIds)}`;
}
